import { FlatInfo } from '@models/flat-info.model';
import { EntryCodeClientService } from '@services/api/entry-code/entry-code-client.service';
import { IEntryCodeModel } from '@services/api/entry-code/entry-code-model.interface';
import { LogService } from '@services/util/log.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { ArrivalBookingRow, DepartureBookingRow, GuestBookingRow } from '../models/booking-overview.model';
import { entryCodeForFlat, FlatEntryCodeLookup, toRowEntryCodes } from './entry-code-result';
import { FlatIndex, indexByFlat } from './flat-index';

export type EntryCodeRow = ArrivalBookingRow | GuestBookingRow | DepartureBookingRow;
export type EntryCodeIndex = FlatIndex<IEntryCodeModel>;

/**
 * Belépőkódok a foglalások áttekintéséhez: egyetlen összevont lekérés a még érvényes kódokra,
 * lakásra indexelve (a válasz sorrendjében); a sorok ebből kapják a kódjukat (spec 6.3).
 */
@Injectable({
  providedIn: 'root'
})
export class BookingEntryCodeService {
  private readonly entryCodeClientService = inject(EntryCodeClientService);
  private readonly log = inject(LogService);

  /** Minden lakás még érvényes kódja lakásra indexelve; hibánál `null`, amitől a sorok hibásként jelennek meg. */
  loadEntryCodeIndex(): Observable<EntryCodeIndex | null> {
    return this.entryCodeClientService.getActiveEntryCodesForAllFlats().pipe(
      map((codes) => indexByFlat(codes, (code) => code.flat?.id)),
      catchError((error: unknown) => {
        this.log.error('Failed to load entry codes', error);
        return of(null);
      })
    );
  }

  markEntryCodesLoading(row: EntryCodeRow): void {
    row.entryCodesLoading = true;
  }

  /**
   * A sor kódjai az indexből, a korábbi lakásonkénti logikával azonos eredménnyel (spec 6.1).
   * Vendég-azonosító nélkül a sor hibás; hibaszöveg sosem kerül a kódok közé.
   */
  applyEntryCodes(row: EntryCodeRow, index: EntryCodeIndex | null, label: string): void {
    row.entryCodesLoading = false;
    const guestId = row.guest.id;
    if (!row.flatInfos.length || !guestId) {
      this.log.warn('Cannot load entry codes: No flats or no guest ID', { row });
      row.entryCodes = [];
      row.entryCodesFailed = true;
      return;
    }
    const results = row.flatInfos.map((flatInfo) => entryCodeForFlat(flatInfo.id, guestId, index));
    row.flatInfos.forEach((flatInfo, position) => this.logValidation(label, row, flatInfo, results[position]));
    const rowCodes = toRowEntryCodes(results);
    row.entryCodes = rowCodes.codes;
    row.entryCodesFailed = rowCodes.failed;
  }

  /** A korábbi ENTRY_CODE_VALIDATION napló (docs/entry-code-log-format.md), csak ha a lakásra volt keresés. */
  private logValidation(label: string, row: EntryCodeRow, flatInfo: FlatInfo, result: FlatEntryCodeLookup<IEntryCodeModel>): void {
    if (!result.codes) {
      return;
    }
    this.log.info(`ENTRY_CODE_VALIDATION [${label}]`, {
      flatId: flatInfo.id,
      flatName: flatInfo.flatName,
      guestId: row.guest.id,
      guestName: row.guest.name,
      bookingId: row.id,
      deprecatedLogic: result.codes.length > 0 ? result.codes[0].entry_code : '',
      availableValues: result.codes.map((ec) => ({ code: ec.entry_code, userId: ec.user?.id, userName: ec.user?.name, validTo: ec.valid_to })),
      improvedLogic: result.code
    });
  }
}
